import { DateTime } from "luxon";
import React, { useState, useMemo } from "react";
import {
  Button,
  Col,
  Row,
  Modal,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import { navigate } from "@reach/router";
import useSWR from "swr";
import { Facebook as FacebookLoader } from "react-content-loader";
import { Textfit } from "react-textfit";

import fetcher from "libs/swr";
import axiosObj from "libs/axios";
import { useAppContext } from "libs/contextLib";
import Api from "./Api";

import GenericError from "components/GenericError";
import { NoClasses } from "components/GenericImage";
import LoginModal from "components/LoginModal";
import SubscribeModal from "components/SubscribeModal";

import { STUDENT_URLS, TEACHER_URLS } from "constants/urls";
import { COMMON_ERROR } from "constants/common";
import {
  JOIN_CONFIRMATION_MESSAGE,
  CANCEL_CONFIRMATION_MESSAGE,
} from "../constants/subscribe-modal";

import "./realclass-list.scss";

function RealClassList(props) {
  const { authenticateUser } = useAppContext();

  // If params object is created without useMemo, it is considered as a new object on every render, causing endless api calls
  // https://github.com/vercel/swr/issues/93#issuecomment-552072277
  const getParams = useMemo(
    () => ({
      course: props.courseId,
      date: props.selectedDate.toISODate(),
    }),
    [props.courseId, props.selectedDate]
  );
  const isLearnDomain = props.domain === "learn";
  const classListURL = isLearnDomain
    ? STUDENT_URLS.CLASS_LIST
    : TEACHER_URLS.CLASS_LIST;
  const { data, mutate, error } = useSWR([classListURL, getParams], fetcher);

  if (!data && !error) {
    return (
      <Row className="justify-content-left m-4">
        <Col xs={12} md={10}>
          <FacebookLoader foregroundColor="#fae7e7" />{" "}
          <FacebookLoader foregroundColor="#fae7e7" />{" "}
          <FacebookLoader foregroundColor="#fae7e7" />{" "}
        </Col>
      </Row>
    );
  } else if (error) {
    if (
      error.response &&
      error.response.status &&
      error.response.status === 401
    ) {
      authenticateUser(null);
    }
    // TODO: Show error code specific errors:
    // - Not authorized on 403
    // - Try again on timeout

    return (
      <Row className="justify-content-center">
        <Col xs="auto" style={{ height: "70vh" }}>
          <GenericError />
        </Col>
      </Row>
    );
  } else if (!Array.isArray(data.results) || !data.results.length) {
    return (
      <Row className="justify-content-center">
        <Col xs="auto" style={{ height: "70vh" }} className="no-classes">
          <NoClasses />
          <div className="help-text">
            <Textfit mode="single">
              No classes for today. Breathe and relax.
            </Textfit>
          </div>
        </Col>
      </Row>
    );
  } else {
    return data.results.map((item) => (
      <RealClass
        realClass={item}
        key={item.id}
        domain={props.domain}
        mutateList={mutate}
      />
    ));
  }
}

function GenericModal(props) {
  const [isButtonLoading, setButtonLoading] = useState();
  return (
    <Modal
      show={props.showModal}
      onHide={() => (isButtonLoading ? null : props.handleClose)}
    >
      <Modal.Body>
        <h4>{props.message}</h4>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={props.handleClose}
          disabled={isButtonLoading}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={async () => {
            setButtonLoading(true);
            await props.onConfirm();
            setButtonLoading(false);
          }}
          disabled={isButtonLoading}
          className="confirm-button"
        >
          {isButtonLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
            </>
          ) : null}
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RealClass(props) {
  const {
    authenticatedUser,
    authenticateUser,
    updateErrorMessage,
  } = useAppContext();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const isLearnDomain = props.domain === "learn";

  const onJoinClick = () => setShowJoinModal(true);

  const onCancelClick = () => setShowCancelModal(true);

  async function onJoinConfirm() {
    if (!authenticatedUser) {
      setShowLoginModal(true);
    } else {
      // TODO: set state for loading and use that for showing loader
      try {
        const response = await Api().post(
          isLearnDomain
            ? STUDENT_URLS.CLASS_JOIN(props.realClass.id)
            : TEACHER_URLS.CLASS_JOIN(props.realClass.id)
        );
        setShowJoinModal(false);
        if (response.data.url !== undefined) {
          navigate("/vod", { state: response.data });
        } else {
          navigate(`/${props.domain}/live-stream`, {
            state: response.data,
          });
        }
      } catch (error) {
        setShowJoinModal(false);
        if (error.response && error.response.status) {
          if (error.response.status === 401) {
            authenticateUser(null);
            setShowLoginModal(true);
          } else if (error.response.status === 400) {
            // need to see why 400 is unauthorized?
            setShowSubscribeModal(true);
          }
        } else {
          updateErrorMessage(COMMON_ERROR);
        }
      }
    }
  }

  async function onCancelConfirm(classId) {
    // Call realclass patch api
    // Update/mutate local realclass/list

    try {
      await axiosObj.patch(TEACHER_URLS.CLASS(classId), {
        is_cancelled: "true",
      });
      await props.mutateList();

      // Below code avoids call to list api, but needs to be simplified

      // await props.mutateList(async (realClassList) => {
      //   const cancelledRealClass =
      //     axiosObj.patch(TEACHER_URLS.CLASS(classId), {
      //       is_cancelled: "true",
      //     });
      //
      //   // filter the list, and return it with the updated item
      //   const filteredRealClassList = realClassList.results.filter(
      //     (realClass) => realClass.id !== classId
      //   );
      //   return {
      //     ...realClassList,
      //     results: [...filteredRealClassList, cancelledRealClass.data],
      //   };
      // });
    } catch (error) {
      setShowCancelModal(false);
      if (
        error.response &&
        error.response.status &&
        error.response.status === 401
      ) {
        authenticateUser(null);
        setShowLoginModal(true);
      } else {
        updateErrorMessage(COMMON_ERROR);
      }
    }
    setShowCancelModal(false);
  }

  const tooltipMessage = props.realClass.isCancelled
    ? "Class was cancelled by instructor"
    : !isLearnDomain && !props.realClass.isStreamedLive
    ? "Only live classes can be joined"
    : !props.realClass.isRunning
    ? "Join 5 minutes before the class starts"
    : "";

  const isJoinDisabled =
    !props.realClass.isRunning ||
    props.realClass.isCancelled ||
    (!isLearnDomain && !props.realClass.isStreamedLive);

  const JoinButton = (
    <Button
      className="join-button border rounded text-light w-100 h-100"
      onClick={onJoinClick}
      disabled={isJoinDisabled}
      style={{
        pointerEvents: isJoinDisabled ? "none" : "auto",
      }}
    >
      {props.realClass.isCancelled ? "Cancelled" : "Join"}
    </Button>
  );

  return (
    <>
      <LoginModal
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
      />
      <SubscribeModal
        showSubscribeModal={showSubscribeModal}
        setShowSubscribeModal={setShowSubscribeModal}
      />
      <GenericModal
        message={JOIN_CONFIRMATION_MESSAGE}
        onConfirm={onJoinConfirm}
        showModal={showJoinModal}
        handleClose={() => setShowJoinModal(false)}
      />
      <GenericModal
        message={CANCEL_CONFIRMATION_MESSAGE}
        onConfirm={() => onCancelConfirm(props.realClass.id)}
        showModal={showCancelModal}
        handleClose={() => setShowCancelModal(false)}
      />

      <Row className="real-class-card mt-4 p-2 mx-auto bg-white border rounded">
        <Col className="col-12 col-md-2 p-0 real-class-cover-image">
          <img
            alt="profile"
            src={props.realClass.imageUrls[0]}
            style={{
              objectFit: "cover",
              width: "100%",
              height: "100%",
            }}
            className="img-fluid rounded"
          />
        </Col>
        <Col className="col-12 col-md-10">
          <Row className="justify-content-between align-items-center">
            <Col className="class-name col-auto">{props.realClass.name}</Col>
            <Col className="col-auto class-type rounded">
              {props.realClass.isStreamedLive ? "Live" : "Rec"}
            </Col>
          </Row>
          <Row className="justify-content-between">
            <Col xs={12} md={9}>
              {!isLearnDomain ? (
                <br />
              ) : (
                <div>
                  by{" "}
                  <span className="teacher-name">
                    {props.realClass.teacher.firstName +
                      " " +
                      props.realClass.teacher.lastName}
                  </span>
                </div>
              )}
              <div className="mt-1">
                <span className="schedule border rounded p-1">
                  {DateTime.fromSeconds(props.realClass.startTime)
                    .setLocale("en-US")
                    .toLocaleString(DateTime.TIME_SIMPLE)}
                  , {props.realClass.duration / 60} mins
                </span>
              </div>
              <div className="mt-1 class-description text-truncate text-muted">
                {props.realClass.description}
              </div>
            </Col>
            <Col xs={12} md={3} className="align-self-end mt-3 px-0 mx-0">
              <Col xs={12} className="p-0 m-0">
                {tooltipMessage ? (
                  <OverlayTrigger overlay={<Tooltip>{tooltipMessage}</Tooltip>}>
                    <div className="w-100 h-100">{JoinButton}</div>
                  </OverlayTrigger>
                ) : (
                  JoinButton
                )}
              </Col>
              {isLearnDomain || !props.realClass.canCancel ? null : (
                <Col xs={12} className="p-0 m-0">
                  <Button
                    className="cancel-button w-100 h-100"
                    variant="link"
                    onClick={onCancelClick}
                  >
                    Need to cancel?
                  </Button>
                </Col>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}

export default RealClassList;
